import React from 'react';
import { Typography } from '@heycater/design-system';
import { useRouter } from 'next/router';
import { Trans, useTranslation } from 'react-i18next';
import styled from 'styled-components';

import DrinksImg from '@images/homepage/drinks.png';
import HeyEventsImg from '@images/homepage/heyevents.jpg';
import RegularCateringImg from '@images/homepage/regular.png';
import TeamEventsImg from '@images/homepage/team.jpg';
import { currentMonthYear } from 'shared/helpers/dates';
import {
  CardsArray,
  CardsSlider,
} from 'static_pages/home/components/CardsSlider';

export default function ProductsSelection() {
  const { t } = useTranslation('homepage');
  const router = useRouter();
  const openLink = (link: string) => {
    window.open(link, '_blank');
  };
  const { month, year } = currentMonthYear();

  const scrollToQF = (link: string) => {
    router.push(link);
  };

  const cards: CardsArray = [
    {
      title: t('productSelection.card1.title'),
      description: t('productSelection.card1.description'),
      image: RegularCateringImg,
      chips: [
        t('productSelection.card1.chips.1'),
        t('productSelection.card1.chips.2'),
        t('productSelection.card1.chips.3'),
        t('productSelection.card1.chips.4'),
      ],
      subtitle: (
        <Trans
          t={t}
          i18nKey="productSelection.card1.subtitle"
          components={[<strong key="strong" />]}
        />
      ),
      moreLink: 'https://www.heycater.com/heykantine',
      moreLinkText: t('productSelection.card1.learnMoreText'),
      cta: {
        title: t('productSelection.card1.cta'),
        variant: 'secondary',
        onClick: () =>
          openLink(
            `https://calendly.com/food-program/beratungsgesprach-heykantine?month=${year}-${month}`
          ),
        link: '#qf-start',
      },
    },
    {
      title: t('productSelection.card2.title'),
      description: t('productSelection.card2.description'),
      image: HeyEventsImg,
      chips: [
        t('productSelection.card2.chips.1'),
        t('productSelection.card2.chips.2'),
        t('productSelection.card2.chips.3'),
        t('productSelection.card2.chips.4'),
      ],
      subtitle: (
        <Trans
          t={t}
          i18nKey="productSelection.card2.subtitle"
          components={[<strong key="strong" />]}
        />
      ),
      moreLink: 'https://www.heycater.com/events',
      moreLinkText: t('productSelection.card2.learnMoreText'),
      cta: {
        title: t('productSelection.card2.cta'),
        variant: 'secondary',
        onClick: () => openLink('https://www.heycater.com/de/event-contact'),
        link: '#qf-start',
      },
    },
    {
      title: t('productSelection.card3.title'),
      description: t('productSelection.card3.description'),
      image: TeamEventsImg,
      chips: [
        t('productSelection.card3.chips.1'),
        t('productSelection.card3.chips.2'),
        t('productSelection.card3.chips.3'),
        t('productSelection.card3.chips.4'),
      ],
      subtitle: (
        <Trans
          t={t}
          i18nKey="productSelection.card3.subtitle"
          components={[<strong key="strong" />]}
        />
      ),
      moreLink: 'https://www.heycater.com/de/business-catering',
      moreLinkText: t('productSelection.card3.learnMoreText'),
      cta: {
        title: t('productSelection.card3.cta'),
        variant: 'secondary',
        onClick: scrollToQF,
        link: '#qf-start',
      },
    },
    {
      title: t('productSelection.card4.title'),
      description: t('productSelection.card4.description'),
      image: DrinksImg,
      chips: [
        t('productSelection.card4.chips.1'),
        t('productSelection.card4.chips.2'),
        t('productSelection.card4.chips.3'),
        t('productSelection.card4.chips.4'),
      ],
      subtitle: (
        <Trans
          t={t}
          i18nKey="productSelection.card4.subtitle"
          components={[<strong key="strong" />]}
        />
      ),
      moreLink: 'https://www.heycatermarketing.com/drink-snacks-service',
      moreLinkText: t('productSelection.card4.learnMoreText'),
      cta: {
        title: t('productSelection.card4.cta'),
        variant: 'secondary',
        onClick: () =>
          openLink(
            `https://calendly.com/food-program/beratungsgesprach-pantry-service?${year}-${month}`
          ),
        link: '#qf-start',
      },
    },
  ];

  return (
    <Wrapper>
      <HeaderBox>
        <Title mb={2}>{t('productSelection.title')}</Title>
        <Subtitle>{t('productSelection.subtitle')}</Subtitle>
      </HeaderBox>
      <CardsSlider cards={cards} />
    </Wrapper>
  );
}

const Title = styled(Typography)`
  font-weight: 700;
  font-size: 36px;
  line-height: 62.1px;
  text-align: center;
  ${({ theme }) => theme.breakpoints.up('md')} {
    font-size: 32px;
    text-align: center;
    line-height: 48px;
  }
`;

const HeaderBox = styled.div`
  padding: ${({ theme }) => theme.spacing(0, 2)};
  ${({ theme }) => theme.breakpoints.up('md')} {
    margin-bottom: ${({ theme }) => theme.spacing(2)}px;
  }
`;

const Subtitle = styled(Typography)`
  font-size: 16px;
  line-height: 26.88px;
  text-align: center;
`;

const Wrapper = styled.div`
  width: 100%;
  padding-bottom: 54px;
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(4)}px;
  .slick-arrow {
    color: #3c4340;
  }
  margin: auto;
  ${({ theme }) => theme.breakpoints.up('md')} {
    width: 100%;
    max-width: 1310px;
    margin: 0 auto;
  }
`;
